import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useSubscription } from '@/context/SubscriptionContext';

/**
 * Crisp Live-Chat mit manuellem Cookiebot-Consent-Gate.
 *
 * WARUM MANUELL: Cookiebot data-blockingmode="auto" ist in einer CRA-SPA
 * unzuverlaessig (Scripts werden client-seitig injiziert, nicht im initialen HTML).
 * Wir laden Crisp deshalb NUR nach explizit erteiltem Consent selbst.
 *
 * CONSENT-KATEGORIE: Crisp ist ein funktionaler Support-/Live-Chat.
 * Cookiebot-Setup (Dashboard geprueft): Necessary=3, Preferences=0, Statistics=4,
 * Marketing=0. 'Preferences' ist LEER - alles laeuft ueber 'statistics'. Deshalb gaten
 * wir Crisp ausschliesslich auf window.Cookiebot.consent.statistics (einheitlich mit
 * PostHog in PostHogGate.jsx).
 */

// Crisp Website ID (aus Crisp > Settings > Website Settings > Setup).
const CRISP_WEBSITE_ID = '9ba80033-db6c-4736-80ea-a253d7d7a08e';

const hasCrispConsent = () => {
  const c = typeof window !== 'undefined' && window.Cookiebot && window.Cookiebot.consent;
  if (!c) return false;
  // Einheitliche Kategorie: statistics (Preferences ist im Cookiebot-Setup leer).
  return !!c.statistics;
};

const loadCrisp = () => {
  if (typeof window === 'undefined') return;
  // Guard: Placeholder-ID nicht ausliefern (verhindert kaputtes Script in Produktion).
  if (!CRISP_WEBSITE_ID || CRISP_WEBSITE_ID === 'CRISP_WEBSITE_ID') {
    // eslint-disable-next-line no-console
    console.warn('[CrispChat] Consent erteilt, aber CRISP_WEBSITE_ID ist noch Platzhalter - Chat wird nicht geladen.');
    return;
  }
  // Doppel-Laden verhindern.
  if (window.$crisp && document.getElementById('crisp-sdk')) return;

  window.$crisp = window.$crisp || [];
  window.CRISP_WEBSITE_ID = CRISP_WEBSITE_ID;

  const s = document.createElement('script');
  s.id = 'crisp-sdk';
  s.src = 'https://client.crisp.chat/l.js';
  s.async = 1;
  document.head.appendChild(s);
};

const unloadCrisp = () => {
  if (typeof window === 'undefined' || !window.$crisp) return;
  // Crisp bietet kein vollstaendiges Unload ohne Reload. Best effort:
  // Session zuruecksetzen + Widget verstecken. Vollstaendige Entfernung erst beim naechsten Reload,
  // da dann das Consent-Gate den Load verhindert.
  try {
    window.$crisp.push(['do', 'session:reset']);
    window.$crisp.push(['do', 'chat:hide']);
  } catch (e) {
    /* noop */
  }
};

export default function CrispChat() {
  const { user } = useAuth();
  const { plan } = useSubscription();
  const loadedRef = useRef(false);
  const [consented, setConsented] = useState(false);

  // 1) Consent-Handling: initial pruefen + auf Cookiebot-Events hoeren.
  useEffect(() => {
    const applyConsent = () => {
      if (hasCrispConsent()) {
        if (!loadedRef.current) {
          loadCrisp();
          loadedRef.current = true;
        }
        setConsented(true);
      } else {
        if (loadedRef.current) {
          unloadCrisp();
          loadedRef.current = false;
        }
        setConsented(false);
      }
    };

    // Falls Cookiebot bereits vor dem React-Mount ausgeloest hat.
    applyConsent();

    window.addEventListener('CookiebotOnAccept', applyConsent);
    window.addEventListener('CookiebotOnDecline', applyConsent);
    return () => {
      window.removeEventListener('CookiebotOnAccept', applyConsent);
      window.removeEventListener('CookiebotOnDecline', applyConsent);
    };
  }, []);

  // 2) User-Daten in die Crisp-Session pushen, sobald Consent + eingeloggt.
  // Laeuft neu, wenn Consent nachtraeglich erteilt wird oder sich User/Plan aendern.
  useEffect(() => {
    if (!consented || !user || !window.$crisp) return;

    if (user.email) window.$crisp.push(['set', 'user:email', [user.email]]);
    if (user.name) window.$crisp.push(['set', 'user:nickname', [user.name]]);

    const sessionData = [];
    const projectId = user.active_project_id;
    if (projectId) sessionData.push(['project_id', projectId]);
    if (plan) sessionData.push(['plan', plan]);
    if (sessionData.length) window.$crisp.push(['set', 'session:data', [sessionData]]);
  }, [consented, user, plan]);

  return null;
}
